import React, { useState, memo, useEffect } from 'react';
import styled from 'styled-components';

import { Address } from '@front/common/src/types/commons';
import { EMPTY_ADDRESS } from '@front/common/src/constants/address';
import { buildWoosmapAddress, isValidWoosmapAddress } from '@front/common/src/utils/address';
import { CommonInputProps } from '@front/common/src/components/input/types';
import {
  AutocompleteResponseItem,
  useAddressDetailQuery,
  useAddressQuery,
} from '@front/common/src/queries/woosmap/address-queries';
import { TrackLocationEvent } from '@front/common/src/types/tracking';

import { translate } from '@front/i18n';

import { Suggestion, autocompleteDropdownStyle } from '../../styles/autocomplete';
import Text from '../base-text/variants/text';
import Input from '../input/input';

const t = translate('components.autocomplete_address');

const AutoCompleteDropdown = styled.div`
  ${autocompleteDropdownStyle}
`;

const Container = styled.div`
  position: relative;
  width: 100%;
`;

export type WoosmapAutocompleteProps = Pick<CommonInputProps, 'error'> & {
  label?: string;
  formattedAddress?: string;
  onEvent?: (event: TrackLocationEvent) => void;
  id?: string;
  name?: string;
  setAddressError: (message?: string) => void;
  apiKey: string;
  setAddressFields: (address: Partial<Address>) => void;
};

export const WoosmapAutocomplete = ({
  label = t('inputs.address.label'),
  id = 'formLocationSelection',
  name = 'addressAutocomplete',
  formattedAddress = '',
  onEvent,
  setAddressError,
  setAddressFields,
  error,
  apiKey,
}: WoosmapAutocompleteProps) => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const { data: suggestions } = useAddressQuery({
    keyword: searchKeyword,
    apiKey,
  });

  const [selectedSuggestion, setSelectedSuggestion] = useState<AutocompleteResponseItem>();

  // This query is triggered only when we have a selectedSuggestion.public_id
  // So this means, it will be enabled only when a suggestion on the dropdown is clicked
  const { addressDetail } = useAddressDetailQuery({ publicId: selectedSuggestion?.public_id, apiKey });

  useEffect(() => {
    if (addressDetail) {
      if (isValidWoosmapAddress(addressDetail)) {
        setAddressFields(buildWoosmapAddress(addressDetail));
        setSearchKeyword('');
      } else {
        setAddressError(t('.errors.address'));
        if (onEvent) onEvent('UnableToGeolocalize');
      }
    }
    // We only want this effect to be triggered when the addressDetail change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressDetail]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddressFields(EMPTY_ADDRESS);
    setSelectedSuggestion(undefined);
    setSearchKeyword(e.target.value);
  };

  return (
    <Container>
      <Input
        id={id}
        name={name}
        autoComplete='off'
        label={label}
        error={error}
        onChange={handleChange}
        value={searchKeyword.length > 0 ? searchKeyword : formattedAddress}
      />
      {suggestions && suggestions.length > 0 && (
        <AutoCompleteDropdown className='autocomplete-dropdown-container' role='listbox'>
          {suggestions.map((suggestion) => (
            <Suggestion key={suggestion.public_id} onClick={() => setSelectedSuggestion(suggestion)} role='option'>
              <Text>{suggestion.description}</Text>
            </Suggestion>
          ))}
        </AutoCompleteDropdown>
      )}
    </Container>
  );
};

export default memo(WoosmapAutocomplete);
